import { GenericActionCard } from '@/components/companion/feed/presenters/GenericActionCard';
import type {
  CompanionPresenter,
  CompanionPresenterProps,
} from '@/components/companion/feed/presenters/presenter-types';
import type { CompanionActionDto, CompanionActionKind, CompanionEvidence } from '@fluenceur/types';
import type { ComponentType } from 'react';

/**
 * Narrows the card to the evidence its presenter reads.
 *
 * A kind and its evidence are stamped together at publication, so the mismatch
 * below should be impossible. It is handled anyway: a card whose evidence does
 * not match its kind falls back to the generic presenter rather than crashing
 * the whole panel on a field that is not there.
 *
 * Written once and applied per kind, because the five hand-written versions had
 * already drifted: two of them dropped a prop the other three forwarded. The
 * spread makes that class of omission impossible.
 */

/** A card whose evidence the kind has already been checked against. */
export type CompanionCardProps<K extends CompanionActionKind> = Omit<
  CompanionPresenterProps,
  'action'
> & {
  action: CompanionActionDto & { evidence: Extract<CompanionEvidence, { kind: K }> };
  flat?: boolean;
};

export const createCompanionPresenter = <K extends CompanionActionKind>(
  kind: K,
  Card: ComponentType<CompanionCardProps<K>>
): CompanionPresenter => {
  const Presenter = (props: CompanionPresenterProps) => {
    const { action } = props;
    if (action.evidence.kind !== kind) return <GenericActionCard {...props} />;

    return (
      <Card
        {...props}
        action={{ ...action, evidence: action.evidence as Extract<CompanionEvidence, { kind: K }> }}
        flat
      />
    );
  };

  Presenter.displayName = `CompanionPresenter(${kind})`;
  return Presenter;
};
